// src/components/FilterBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  TextField,
  Autocomplete,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const FilterBar = ({
  modifiedDate,
  onModifiedDateChange,
  types,
  selTypes,
  onSelTypesChange,
  owners,
  selOwners,
  onSelOwnersChange,
  dealName,
  onDealNameChange,
  adcoOnly = false,
  onAdcoOnlyChange = () => {}
}) => {
  const theme = useTheme();
  const [localSelTypes, setLocalSelTypes] = useState(selTypes);
  const [localSelOwners, setLocalSelOwners] = useState(selOwners);
  const typesTimerRef = useRef(null);
  const ownersTimerRef = useRef(null);

  // Sync local state with props when they change externally
  useEffect(() => {
    setLocalSelTypes(selTypes);
  }, [selTypes]);

  useEffect(() => {
    setLocalSelOwners(selOwners);
  }, [selOwners]);

  const handleTypesChange = (event, newValue) => {
    setLocalSelTypes(newValue);
    console.log('🎯 Asset type filter changed:', newValue);
    
    // Clear existing timer
    if (typesTimerRef.current) {
      clearTimeout(typesTimerRef.current);
    }
    
    // Set new timer for debounced update
    typesTimerRef.current = setTimeout(() => {
      onSelTypesChange(newValue);
    }, 500);
  };

  const handleOwnersChange = (event, newValue) => {
    setLocalSelOwners(newValue);
    console.log('👤 Security owner filter changed:', newValue);
    
    // Clear existing timer
    if (ownersTimerRef.current) {
      clearTimeout(ownersTimerRef.current);
    }
    
    // Set new timer for debounced update
    ownersTimerRef.current = setTimeout(() => {
      onSelOwnersChange(newValue);
    }, 500);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (typesTimerRef.current) {
        clearTimeout(typesTimerRef.current);
      }
      if (ownersTimerRef.current) {
        clearTimeout(ownersTimerRef.current);
      }
    };
  }, []);

    return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <DatePicker
              label="Modified Date"
              value={modifiedDate}
          onChange={onModifiedDateChange}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: {
                '& .MuiInputBase-root': {
                  height: 56,
                  fontSize: '15px'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  fontWeight: 500
                }
              }
            }
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Autocomplete
          multiple
          id="asset-type-autocomplete"
          options={types}
          getOptionLabel={(option) => option}
          value={localSelTypes}
          onChange={handleTypesChange}
          noOptionsText={types.length === 0 ? "Loading asset types..." : "No asset types found"}
          disableCloseOnSelect
          clearOnBlur={false}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                checked={selected}
                sx={{
                  '&.Mui-checked': {
                    color: theme.palette.primary.main
                  }
                }}
              />
              <ListItemText primary={option} />
            </li>
          )}
          renderTags={(value, getTagProps) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {value.slice(0, 2).map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    bgcolor: alpha(theme.palette.secondary.main, 0.15),
                    color: theme.palette.secondary.dark,
                    fontWeight: 600
                  }}
                />
              ))}
              {value.length > 2 && (
                <Chip
                  label={`+${value.length - 2} more`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    bgcolor: '#f3f4f6',
                    color: '#6b7280'
                  }}
                />
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Asset Type"
              placeholder={localSelTypes.length === 0 ? "Search asset types..." : ""}
              sx={{
                '& .MuiInputBase-root': {
                  height: 56,
                  fontSize: '15px'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  fontWeight: 500
                }
              }}
            />
          )}
          sx={{
            minWidth: 250
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Autocomplete
          multiple
          id="security-owner-autocomplete"
          options={owners}
          getOptionLabel={(option) => option}
          value={localSelOwners}
          onChange={handleOwnersChange}
          noOptionsText={owners.length === 0 ? "Loading security owners..." : "No security owners found"}
          disableCloseOnSelect
          clearOnBlur={false}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                checked={selected}
                sx={{
                  '&.Mui-checked': {
                    color: theme.palette.primary.main
                  }
                }}
              />
              <ListItemText primary={option} />
            </li>
          )}
          renderTags={(value, getTagProps) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {value.slice(0, 2).map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    color: theme.palette.success.dark,
                    fontWeight: 600
                  }}
                />
              ))}
              {value.length > 2 && (
                <Chip
                  label={`+${value.length - 2} more`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    bgcolor: '#f3f4f6',
                    color: '#6b7280'
                  }}
                />
              )}
          </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Security Owner"
              placeholder={localSelOwners.length === 0 ? "Search security owners..." : ""}
              sx={{
                '& .MuiInputBase-root': {
                  height: 56,
                  fontSize: '15px'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  fontWeight: 500
                }
              }}
            />
          )}
          sx={{
            minWidth: 250
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2 }}>
        <TextField
          label="Deal Name"
          fullWidth
          value={dealName}
          onChange={e => onDealNameChange(e.target.value)}
          placeholder="Filter deals..."
          // A browser or password-manager autofill was writing a saved value into this filter
          // when the Add/Edit Security modal closed; it is a search box, never a credential.
          autoComplete="off"
          slotProps={{ htmlInput: { 'data-1p-ignore': true, 'data-lpignore': 'true', 'data-bwignore': true } }}
          sx={{
            '& .MuiInputBase-root': {
              height: 56,
              fontSize: '15px'
            },
            '& .MuiInputLabel-root': {
              fontSize: '16px',
              fontWeight: 500
            }
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 2 }}>
        <Tooltip title="Show RMBS securities eligible for ADCO modeling" arrow>
          <Paper
            variant="outlined"
            sx={{
              height: 56,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              borderColor: adcoOnly ? 'primary.main' : 'divider',
              bgcolor: adcoOnly ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
            }}
          >
            <FormControlLabel
              control={(
                <Checkbox
                  checked={adcoOnly}
                  onChange={event => onAdcoOnlyChange(event.target.checked)}
                  inputProps={{ 'aria-label': 'ADCO Enabled' }}
                />
              )}
              label={(
                <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.2 }}>
                  ADCO Enabled
                </Typography>
              )}
              sx={{ m: 0 }}
            />
          </Paper>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default FilterBar;
